import { ScrollReveal } from '@/components/effects/ScrollReveal';
import { Check, ArrowRight } from 'lucide-react';
import { PLANS } from '@/data/plans';

// Extra-domain add-on pricing is funnel display copy (not part of the shared
// plan catalog) — kept in sync with the same constant on /watch.
const EXTRA_DOMAIN_GBP = { essential: 19, professional: 29, business: 39 };

// Single source of truth: derive the homepage tier cards from src/data/plans.js
// so this section stays in lockstep with /watch and the dashboard
// upgrade picker. The "Most Popular" badge has been dropped in favour of the
// per-plan value proposition; mostPopular now only drives visual emphasis.
const tiers = Object.entries(PLANS).map(([id, plan]) => ({
  id,
  name: plan.name,
  price: `£${plan.price.monthly}`,
  extraDomainGbp: EXTRA_DOMAIN_GBP[id],
  href: `/auth?plan=${id}`,
  highlight: Boolean(plan.mostPopular),
  valueProp: plan.valueProp,
  features: plan.features,
}));

function TierCard({ tier }) {
  const priceColor = tier.highlight ? 'var(--a)' : 'var(--white)';

  return (
    <div
      data-testid={`active-guard-tier-${tier.id}`}
      className="relative flex flex-col h-full transition-all duration-200"
      style={{
        background: 'var(--l1)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: tier.highlight ? '1px solid var(--a-border)' : '1px solid var(--b1)',
        borderRadius: '16px',
        padding: '2rem',
        boxShadow: tier.highlight ? '0 0 40px rgba(139,92,246,0.15)' : 'none',
        transform: tier.highlight ? 'scale(1.03)' : 'scale(1)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--a-border)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = tier.highlight ? 'var(--a-border)' : 'var(--b1)';
      }}
    >
      <p
        className="font-mono uppercase mb-3"
        style={{
          fontSize: '0.65rem',
          letterSpacing: '0.15em',
          color: tier.highlight ? 'var(--a)' : 'var(--ts)',
        }}
      >
        {tier.id}
      </p>

      <h3
        className="font-display mb-2"
        style={{
          fontWeight: 600,
          fontSize: '1.25rem',
          color: 'var(--white)',
          letterSpacing: '-0.01em',
        }}
      >
        {tier.name}
      </h3>

      <p
        className="font-body mb-4"
        style={{ fontSize: '0.85rem', color: 'var(--tm)', lineHeight: 1.55 }}
      >
        {tier.valueProp}
      </p>

      <div className="mb-2">
        <div className="flex items-baseline gap-1">
          <span
            className="font-display"
            style={{
              fontWeight: 800,
              fontSize: 'clamp(1.6rem, 2.5vw, 2rem)',
              color: priceColor,
              lineHeight: 1,
              letterSpacing: '-0.02em',
            }}
          >
            {tier.price}
          </span>
          <span
            className="font-body"
            style={{ fontSize: '0.75rem', color: 'var(--ts)' }}
          >
            /month
          </span>
        </div>
        <p
          className="font-body mt-1.5"
          style={{ fontSize: '0.7rem', color: 'var(--ts)' }}
        >
          +£{tier.extraDomainGbp}/mo per extra domain
        </p>
      </div>

      <ul className="space-y-3 mb-4 mt-5 flex-1">
        {tier.features.map((feature) => (
          <li key={feature} className="flex items-start gap-2.5">
            <Check
              size={16}
              style={{ color: 'var(--ok)', flexShrink: 0, marginTop: '2px' }}
            />
            <span
              className="font-body"
              style={{ fontSize: '0.88rem', color: 'var(--tm)', lineHeight: 1.55 }}
            >
              {feature}
            </span>
          </li>
        ))}
      </ul>

      <a
        href={tier.href}
        data-testid={`active-guard-cta-${tier.id}`}
        className="btn-shimmer inline-flex items-center justify-center font-body font-medium text-sm h-12 px-6 rounded-[3px] transition-all duration-200"
        style={
          tier.highlight
            ? {
                background: 'var(--a)',
                color: 'var(--white)',
              }
            : {
                background: 'transparent',
                border: '1px solid var(--b2)',
                color: 'var(--white)',
              }
        }
        onMouseEnter={(e) => {
          if (tier.highlight) {
            e.currentTarget.style.background = 'var(--a-bright)';
          } else {
            e.currentTarget.style.borderColor = 'var(--a-border)';
          }
        }}
        onMouseLeave={(e) => {
          if (tier.highlight) {
            e.currentTarget.style.background = 'var(--a)';
          } else {
            e.currentTarget.style.borderColor = 'var(--b2)';
          }
        }}
      >
        Start Free Trial
        <ArrowRight size={14} className="ml-2" />
      </a>
    </div>
  );
}

export const AcodiaWatchSection = () => {
  return (
    <section
      data-testid="active-guard-section"
      className="relative overflow-hidden"
      style={{
        background:
          'linear-gradient(180deg, rgba(4,3,8,1) 0%, rgba(8,4,16,1) 50%, rgba(4,3,8,1) 100%)',
      }}
    >
      {/* Subtle purple radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 60% 40% at 50% 50%, rgba(139,92,246,0.06) 0%, transparent 70%)',
        }}
      />

      <div className="relative max-w-6xl mx-auto px-6 py-24">
        {/* Header */}
        <ScrollReveal>
          <div className="text-center mb-14">
            <p
              className="font-mono uppercase mb-4"
              style={{
                fontSize: '0.7rem',
                letterSpacing: '0.15em',
                color: 'var(--a)',
              }}
            >
              Acodia Watch
            </p>
            <h2
              className="font-display tracking-[-0.03em] mb-5"
              style={{
                fontWeight: 800,
                fontSize: 'clamp(2rem, 4.5vw, 3.5rem)',
                color: 'var(--white)',
                lineHeight: 1.05,
              }}
            >
              Stop Checking. Start Monitoring.
            </h2>
            <p
              className="font-body font-light max-w-2xl mx-auto"
              style={{
                fontSize: '1rem',
                color: 'var(--tm)',
                lineHeight: 1.72,
              }}
            >
              Acodia Watch monitors your website around the clock so you never have to run another manual scan. The moment something changes — you know. The moment a threat appears — you are alerted.
            </p>
          </div>
        </ScrollReveal>

        {/* Tier cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-5 lg:gap-8 items-stretch">
          {tiers.map((tier, i) => (
            <ScrollReveal key={tier.id} delay={0.15 * (i + 1)}>
              <TierCard tier={tier} />
            </ScrollReveal>
          ))}
        </div>

        {/* Promo teaser */}
        <ScrollReveal delay={0.55}>
          <div className="text-center mt-10">
            <p
              className="font-body"
              style={{ fontSize: '0.85rem', color: 'var(--ts)' }}
            >
              Have a promo code?{' '}
              <a
                href="/watch"
                data-testid="active-guard-promo-teaser"
                className="hover:underline"
                style={{ color: 'var(--a)' }}
              >
                Apply at checkout →
              </a>
            </p>
          </div>
        </ScrollReveal>

        {/* Trust line */}
        <ScrollReveal delay={0.6}>
          <div className="text-center mt-6">
            <p
              className="font-body"
              style={{
                fontSize: '0.8rem',
                color: 'var(--ts)',
                lineHeight: 1.7,
              }}
            >
              7-day free trial · Card required · Cancel any time · No setup fees
            </p>
            <p
              className="font-body mt-3"
              style={{
                fontSize: '0.85rem',
                color: 'var(--ts)',
              }}
            >
              Already have Acodia Watch?{' '}
              <a
                href="/login"
                className="underline-offset-2 hover:underline"
                style={{ color: 'var(--a)' }}
              >
                Sign in to your dashboard →
              </a>
            </p>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};
